import React, { useState, useMemo } from "react";
import { Button, Dropdown, Space } from "antd";
import type { MenuProps } from "antd";

import { voidFnc } from "utils/helpers";
import { useChatFilter, ChatFilterItem, CHAT_FILTER_ALL_KEY } from "hooks/useChatFilter";

interface ChatFilterDropdownProps {
	items: ChatFilterItem[];
	selectedIds: string[];
	onChangeSelectedIds: (ids: string[]) => void;
	allLabel: string;
	placeholder?: string;
	disabled?: boolean;
}

const ChatFilterDropdown = ({
	items,
	selectedIds,
	onChangeSelectedIds,
	allLabel,
	placeholder = "Select...",
	disabled = false
}: ChatFilterDropdownProps) => {
	const [open, setOpen] = useState(false);

	const { allSelected, label, selectedKeys, handleSelect, handleDeselect } = useChatFilter({
		items,
		selectedIds,
		setSelectedIds: onChangeSelectedIds,
		allLabel
	});

	const menuItems = useMemo((): MenuProps["items"] => {
		const allOptionId = `chat-filter-all-${allLabel.replace(/\s+/g, "-").toLowerCase()}`;

		const allOption = {
			key: CHAT_FILTER_ALL_KEY,
			icon: (
				<div className={allSelected ? "property-option-container selected" : "property-option-container"}>
					<input
						className="custom-checkbox"
						id={allOptionId}
						type="checkbox"
						onClick={e => e.stopPropagation()}
						checked={allSelected}
						onChange={voidFnc}
					/>
					<label htmlFor={allOptionId} className="custom-checkbox-label">
						{allLabel}
					</label>
				</div>
			)
		};

		const itemOptions = items.map(item => {
			const optionId = `chat-filter-option-${item.id}`;
			const isSelected = !allSelected && selectedIds.includes(item.id);

			return {
				key: item.id,
				icon: (
					<div className={isSelected ? "property-option-container selected" : "property-option-container"}>
						<input
							className="custom-checkbox"
							id={optionId}
							type="checkbox"
							onClick={e => e.stopPropagation()}
							checked={isSelected}
							onChange={voidFnc}
						/>
						<label htmlFor={optionId} className="custom-checkbox-label">
							{item.name}
						</label>
					</div>
				)
			};
		});

		return [allOption, ...itemOptions];
	}, [items, selectedIds, allSelected, allLabel]);

	return (
		<Dropdown
			className="property-dropdown"
			trigger={["click"]}
			menu={{
				items: menuItems,
				selectable: true,
				multiple: true,
				onSelect: handleSelect,
				onDeselect: handleDeselect,
				selectedKeys,
				defaultSelectedKeys: selectedKeys
			}}
			onOpenChange={setOpen}
			overlayClassName="property-items-container"
			open={open}
			disabled={disabled}
		>
			<Button className="dropdown-button">
				<Space>
					{label || placeholder}
					<img
						src={`${process.env.PUBLIC_URL}/assets/images/icons/black-chevron-down.svg`}
						style={{ width: "1em", height: "1em" }}
						className="chevron-button"
					/>
				</Space>
			</Button>
		</Dropdown>
	);
};

export default ChatFilterDropdown;
export type { ChatFilterItem, ChatFilterDropdownProps };
